export type AddressFormState = {
  address_line1: string;
  address_line2: string;
  landmark: string;
  city: string;
  district: string;
  state: string;
  pincode: string;
  country: string;
};

export const emptyAddressForm: AddressFormState = {
  address_line1: "",
  address_line2: "",
  landmark: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  country: "India",
};

export function formatPostalAddress(form: AddressFormState): string {
  const parts: string[] = [];
  const line1 = form.address_line1.trim();
  const line2 = form.address_line2.trim();
  const landmark = form.landmark.trim();
  const city = form.city.trim();
  const district = form.district.trim();
  const state = form.state.trim();
  const pincode = form.pincode.trim();
  const country = form.country.trim() || "India";

  if (line1) parts.push(line1);
  if (line2) parts.push(line2);
  if (landmark) parts.push(landmark);

  const locality = [city, district].filter(Boolean).join(", ");
  if (locality) parts.push(locality);

  const region = [state, pincode].filter(Boolean).join(" ");
  if (region) parts.push(region);
  if (country) parts.push(country);

  return parts.join(", ");
}

export function hasStructuredAddress(fields: {
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
}): boolean {
  return Boolean(fields.address_line1 || fields.city || fields.state || fields.pincode);
}

export function addressFieldsFromEntity(entity: {
  address_line1?: string | null;
  address_line2?: string | null;
  landmark?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  location?: string | null;
  current_location?: string | null;
}): AddressFormState {
  const legacyLocation = entity.location ?? entity.current_location ?? "";
  const structured = hasStructuredAddress(entity);
  return {
    address_line1: entity.address_line1 ?? "",
    address_line2: entity.address_line2 ?? "",
    landmark: entity.landmark ?? "",
    city: entity.city ?? (structured ? "" : legacyLocation),
    district: entity.district ?? "",
    state: entity.state ?? "",
    pincode: entity.pincode ?? "",
    country: entity.country ?? "India",
  };
}

export function formatDisplayAddress(
  entity: {
    address_line1?: string | null;
    address_line2?: string | null;
    landmark?: string | null;
    city?: string | null;
    district?: string | null;
    state?: string | null;
    pincode?: string | null;
    country?: string | null;
    location?: string | null;
    current_location?: string | null;
  },
  fallback = "—",
): string {
  if (hasStructuredAddress(entity)) {
    return formatPostalAddress(addressFieldsFromEntity(entity));
  }
  const legacy = entity.location?.trim() || entity.current_location?.trim();
  return legacy || fallback;
}

export function primaryCityFromEntity(entity: { city?: string | null; location?: string | null; current_location?: string | null }): string {
  return entity.city?.trim() || entity.location?.trim() || entity.current_location?.trim() || "";
}
